import { Dispatch, SetStateAction, useState } from "react";

import { ErrorView } from "@/components/_molecules/error-view";
import { Loading } from "@/components/_molecules/loading-view";
import { useIsFeatureFlagOn } from "@/domain/_shared/feature-flag/hooks/use-is-feature-flag-on";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { MunicipalPropertyCatalogFilterBar } from "@/domain/municipal-property/components/municipal-property-catalog-filter-bar";
import { MunicipalPropertyCatalogTable } from "@/domain/municipal-property/components/municipal-property-catalog-table";
import { MunicipalPropertyManageDialog } from "@/domain/municipal-property/components/municipal-property-manage-dialog";
import { useMunicipalPropertyList } from "@/domain/municipal-property/hooks/api/use-municipal-property-list";
import { useDebouncedSearchFilter } from "@/domain/municipal-property/hooks/use-debounced-search-filter";
import { EMPTY_MUNICIPAL_PROPERTY_CATALOG_FILTER } from "@/domain/municipal-property/models/empty-municipal-property-catalog-filter";
import { isMunicipalPropertyCatalogFiltered } from "@/domain/municipal-property/models/is-municipal-property-catalog-filtered";
import { MunicipalPropertyCatalogFilter } from "@/domain/municipal-property/models/municipal-property-catalog-filter.model";
import { MunicipalPropertyCatalogRow } from "@/domain/municipal-property/models/municipal-property-catalog-row.model";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID } from "@ark/shared";

export const MunicipalPropertyCatalogPage = () => {
  const consumption: ReturnType<typeof useIsFeatureFlagOn> = useIsFeatureFlagOn({
    flag: MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID,
    isDevelopmentBypassAllowed: false,
  });
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { t } = useDomainTranslation("municipal-property");
  const { filter, setFilter, displayedFilter, handleSearchChange }: ReturnType<typeof useDebouncedSearchFilter<MunicipalPropertyCatalogFilter>> =
    useDebouncedSearchFilter<MunicipalPropertyCatalogFilter>(EMPTY_MUNICIPAL_PROPERTY_CATALOG_FILTER);
  const [selectedProperty, setSelectedProperty]: [
    MunicipalPropertyCatalogRow | null,
    Dispatch<SetStateAction<MunicipalPropertyCatalogRow | null>>,
  ] = useState<MunicipalPropertyCatalogRow | null>(null);
  const { hasPermission: canManageWorkspace }: ReturnType<typeof useCheckPermission> = useCheckPermission({
    resource: "workspace",
    action: "manage",
    resourceId: currentWorkspace.id,
  });

  const { propertyList, totalCount, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useMunicipalPropertyList({
      workspaceId: currentWorkspace.id,
      filter: consumption.isOn ? filter : { ...filter, usage: "ALL" },
    });

  const hasLoadedRows = propertyList.length > 0;
  const shouldShowInitialError = !!error && !hasLoadedRows;

  return (
    <div className="flex h-full max-h-full flex-col overflow-y-hidden">
      <MunicipalPropertyCatalogFilterBar
        filter={displayedFilter}
        municipalityList={currentWorkspace.municipalityList ?? []}
        onSearchChange={handleSearchChange}
        onMunicipalityChange={(municipalityId) => setFilter((current) => ({ ...current, municipalityId }))}
        onUsageChange={(usage: MunicipalPropertyCatalogFilter["usage"]): void =>
          setFilter(
            (current: MunicipalPropertyCatalogFilter): MunicipalPropertyCatalogFilter => ({ ...current, usage }),
          )
        }
        onLifecycleChange={(lifecycle) => setFilter((current) => ({ ...current, lifecycle }))}
      />
      {isLoading ? <Loading /> : null}
      {shouldShowInitialError ? <ErrorView /> : null}
      {!isLoading && !error && !hasLoadedRows ? (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          {isMunicipalPropertyCatalogFiltered(filter) ? t("catalog.emptyFiltered") : t("catalog.empty")}
        </div>
      ) : null}
      {!isLoading && hasLoadedRows ? (
        <MunicipalPropertyCatalogTable
          propertyList={propertyList}
          totalCount={totalCount}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
          onManage={canManageWorkspace ? setSelectedProperty : undefined}
        />
      ) : null}
      <MunicipalPropertyManageDialog
        property={selectedProperty}
        onOpenChange={(isOpen: boolean): void => {
          if (!isOpen) setSelectedProperty(null);
        }}
      />
    </div>
  );
};
